// src/lib/schema/base/localBusiness.js

import { shopInfoContainer } from '@/containers/shopInfo.container';
import { cleanObject } from '@/lib/schema';

/**
 * Creates schema for a store location
 * @param {string} storeId - ID or slug of the store
 * @param {object} storeInfo - Store location details
 * @returns {object} - JSON-LD schema for store
 */
export function generateLocalBusinessSchema(storeId, storeInfo) {
  // Basic validation
  if (!storeInfo || !storeId) {
    console.warn('Missing required store information for schema generation');
    return null;
  }

  const {
    baseURL,
    name: shopName,
    phone: defaultPhone
  } = shopInfoContainer;

  // Determine store type - default to CarpetStore
  const storeType = storeInfo.type || 'CarpetStore';

  // Construct store name
  const storeName = storeInfo.name || `${shopName} - ${storeInfo.location || storeId}`;

  // Build the schema
  const schema = {
    '@context': 'https://schema.org',
    '@type': storeType,
    '@id': `${baseURL}/stores/${storeId}#LocalBusiness`,
    'name': storeName,
    'url': `${baseURL}/stores/${storeId}`
  };

  // Add contact information
  if (storeInfo.phone || defaultPhone) {
    schema.telephone = storeInfo.phone || defaultPhone;
  }

  if (storeInfo.email) {
    schema.email = storeInfo.email;
  } else {
    schema.email = 'info@yourcarpet.com';
  }

  // Add image if available
  if (storeInfo.image) {
    schema.image = storeInfo.image.startsWith('http')
      ? storeInfo.image
      : `${baseURL}${storeInfo.image}`;
  }

  // Add address if available
  if (storeInfo.address) {
    schema.address = {
      '@type': 'PostalAddress',
      'streetAddress': storeInfo.address.street || '',
      'addressLocality': storeInfo.address.city || '',
      'postalCode': storeInfo.address.postalCode || '',
      'addressCountry': storeInfo.address.country || ''
    };
  }

  // Add geo coordinates if available
  if (storeInfo.coordinates && storeInfo.coordinates.lat && storeInfo.coordinates.lng) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      'latitude': storeInfo.coordinates.lat,
      'longitude': storeInfo.coordinates.lng
    };
  }

  // Add opening hours if available
  if (storeInfo.openingHours && Array.isArray(storeInfo.openingHours)) {
    schema.openingHoursSpecification = storeInfo.openingHours.map(hours => ({
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': hours.days,
      'opens': hours.opens,
      'closes': hours.closes
    }));
  }

  // Add payment methods
  schema.paymentAccepted = storeInfo.paymentMethods || 'Cash, Credit Card';

  // Add price range if available
  if (storeInfo.priceRange) {
    schema.priceRange = storeInfo.priceRange;
  }

  // Add review if available
  if (storeInfo.rating) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      'ratingValue': storeInfo.rating,
      'reviewCount': storeInfo.reviewCount || 5,
      'bestRating': 5,
      'worstRating': 1
    };
  }

  // Add departments or special offers if available
  if (storeInfo.departments && Array.isArray(storeInfo.departments)) {
    schema.department = storeInfo.departments.map(dept => ({
      '@type': 'Department',
      'name': dept.name,
      'description': dept.description
    }));
  }

  // Add special hours (like holidays) if available
  if (storeInfo.specialOpeningHours && Array.isArray(storeInfo.specialOpeningHours)) {
    if (!schema.openingHoursSpecification) {
      schema.openingHoursSpecification = [];
    }

    storeInfo.specialOpeningHours.forEach(special => {
      schema.openingHoursSpecification.push({
        '@type': 'OpeningHoursSpecification',
        'validFrom': special.validFrom,
        'validThrough': special.validThrough,
        'dayOfWeek': special.days,
        'opens': special.opens,
        'closes': special.closes
      });
    });
  }

  // Add amenities if available
  if (storeInfo.amenities && Array.isArray(storeInfo.amenities)) {
    schema.amenityFeature = storeInfo.amenities.map(amenity => ({
      '@type': 'LocationFeatureSpecification',
      'name': amenity,
      'value': true
    }));
  }

  // Clean the object to remove any null/undefined values
  return cleanObject(schema);
}

/**
 * Creates schema for multiple store locations
 * @param {object} storesData - Data for all stores
 * @param {string} lang - Language code
 * @returns {object} - JSON-LD schema with graph of all stores
 */
export function generateMultipleStoresSchema(storesData, lang) {
  // Validate input
  if (!storesData || !Array.isArray(storesData) || storesData.length === 0) {
    console.warn('Invalid stores data for multiple stores schema generation');
    return null;
  }

  const { baseURL } = shopInfoContainer;

  // Generate schema for each store
  const storeSchemas = storesData
    .map(store => generateLocalBusinessSchema(store.id, store))
    .filter(Boolean);

  if (storeSchemas.length === 0) {
    console.warn('No valid store schemas generated');
    return null;
  }

  // Create a parent organization schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${baseURL}/${lang}/stores#organization`,
    'name': 'YourCarpet.com Stores',
    'url': `${baseURL}/${lang}/stores`,
    'subOrganization': storeSchemas.map(store => ({ '@id': store['@id'] }))
  };

  // Combine all schemas
  return {
    '@context': 'https://schema.org',
    '@graph': [organizationSchema, ...storeSchemas]
  };
}
